import React from 'react';
import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus,
  faMagnifyingGlass,
  faGavel,
  faCoins,
} from '@fortawesome/free-solid-svg-icons';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { revealStagger } from '../../utils/motion';

const STEPS = [
  {
    step: '01',
    icon: faUserPlus,
    title: 'Create Account',
    desc: 'Register as an artist, investor, or collector with instant KYC verification.',
    tag: 'Frictionless',
  },
  {
    step: '02',
    icon: faMagnifyingGlass,
    title: 'Discover Assets',
    desc: 'Explore authenticated masterworks and fractional TitTat ownership tokens.',
    tag: 'Curated',
  },
  {
    step: '03',
    icon: faGavel,
    title: 'Join Live Bidding',
    desc: 'Enter real-time WebSocket auctions with anti-sniping and sub-millisecond execution.',
    tag: 'Real-time',
  },
  {
    step: '04',
    icon: faCoins,
    title: 'Yield & Liquidity',
    desc: 'Collect token dividends, trade shares in secondary markets, or hold for appreciation.',
    tag: 'Dividends',
  },
];

export default function HowItWorksSection() {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.15 });

  return (
    <section className="lp-how" id="how-it-works" ref={ref}>
      <div className="lp-container lp-how-content">
        <div className="lp-section-header">
          <span className="lp-section-badge">How It Works</span>
          <h2 className="lp-section-title">
            Four Steps to <span className="lp-gradient-text">Art Wealth</span>
          </h2>
          <p className="lp-section-desc">
            A frictionless pipeline engineered to empower both master creators and modern portfolio builders.
          </p>
        </div>

        <div className="lp-how-container">
          <motion.div
            className="lp-how-steps"
            variants={revealStagger.container}
            initial="hidden"
            animate={isVisible ? 'visible' : 'hidden'}
          >
            {STEPS.map((step, i) => {
              const cardTheme = i === 2 ? 'lp-card-dark' : 'lp-card-light';
              return (
                <motion.div
                  key={i}
                  className={`lp-how-step ${cardTheme}`}
                  variants={revealStagger.item}
                  whileHover={{ y: -8, transition: { duration: 0.25 } }}
                >
                <div className="lp-step-top">
                  <span className="lp-step-counter">{step.step}</span>
                  <span className="lp-step-tag-mini">{step.tag}</span>
                </div>

                <div className="lp-step-icon-wrap">
                  <div className="lp-step-number">
                    <FontAwesomeIcon icon={step.icon} />
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="lp-step-connector-line" aria-hidden="true">
                      <div className="lp-step-connector-pulse" />
                    </div>
                  )}
                </div>

                <h3 className="lp-step-title">{step.title}</h3>
                <p className="lp-step-desc">{step.desc}</p>
              </motion.div>
            );
          })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
